import React from 'react';
import styled, { keyframes } from 'styled-components';
import * as FiIcons from 'react-icons/fi';
import { DEPLOYED_APPS } from '../../config/deployedApps';
import AppCard from './AppCard';
import { LandingShell, Container, PrimaryLink, GhostLink, Eyebrow, T } from './ui';

const Ico = (n: keyof typeof FiIcons) => FiIcons[n] as React.ComponentType<any>;
const FiArrowRight = Ico('FiArrowRight');
const FiGrid = Ico('FiGrid');
const FiShield = Ico('FiShield');
const FiKey = Ico('FiKey');
const FiZap = Ico('FiZap');
const FiLayers = Ico('FiLayers');
const FiUserCheck = Ico('FiUserCheck');
const FiHome = Ico('FiHome');
const FiActivity = Ico('FiActivity');
const FiHeart = Ico('FiHeart');
const FiSend = Ico('FiSend');
const FiCheckCircle = Ico('FiCheckCircle');
const FiLogIn = Ico('FiLogIn');

/**
 * ServiceBuds company home (`/`). A high-polish marketing landing that showcases
 * the product suite and drives visitors into the preview-access flow. Fully
 * self-contained — inline SVG + system fonts, no external assets.
 */
const CompanyHome: React.FC = () => (
  <LandingShell>
    {/* HERO */}
    <Hero>
      <HeroBlobA /><HeroBlobB /><HeroGrid />
      <Container>
        <HeroInner>
          <HeroText>
            <Kicker>The ServiceBuds platform</Kicker>
            <HeroTitle>
              Run your whole operation on <Grad>one platform</Grad>.
            </HeroTitle>
            <HeroLead>
              Property management, pest-control operations, workplace safety, and clinical
              care — four purpose-built products, one secure sign-in. Give your teams the
              software the job actually needs.
            </HeroLead>
            <HeroCtas>
              <PrimaryLink to="/signup" style={{ background: '#fff', color: T.accentDark, boxShadow: '0 10px 24px rgba(0,0,0,.18)' }}>
                Request preview access <FiArrowRight />
              </PrimaryLink>
              <GhostLink to="/products" style={{ color: '#fff', background: 'rgba(255,255,255,.10)', border: '1px solid rgba(255,255,255,.26)' }}>
                Explore products
              </GhostLink>
            </HeroCtas>
            <Trust>
              <span>Built for teams in</span>
              <Chips>
                <Chip><FiHome /> Property</Chip>
                <Chip><FiActivity /> Pest control</Chip>
                <Chip><FiShield /> Safety</Chip>
                <Chip><FiHeart /> Healthcare</Chip>
              </Chips>
            </Trust>
          </HeroText>
          <HeroArt><DashboardArt /></HeroArt>
        </HeroInner>
      </Container>
    </Hero>

    {/* STATS */}
    <Container>
      <Stats>
        {[
          ['4', 'integrated products'],
          ['1', 'unified platform'],
          ['1', 'secure sign-in'],
          ['HTTPS', 'by default'],
        ].map(([n, l]) => (
          <Stat key={l}><StatN>{n}</StatN><StatL>{l}</StatL></Stat>
        ))}
      </Stats>
    </Container>

    {/* PRODUCTS */}
    <Section>
      <Container>
        <Head>
          <Eyebrow>The product suite</Eyebrow>
          <H2>Four products. One login surface.</H2>
          <Lead2>Each app is deployed and ready — preview the ones the team clears for you.</Lead2>
        </Head>
        <Cards>
          {DEPLOYED_APPS.map(app => <AppCard key={app.id} app={app} />)}
        </Cards>
      </Container>
    </Section>

    {/* PLATFORM — alternating feature rows */}
    <Section $muted>
      <Container>
        <Head>
          <Eyebrow>The platform</Eyebrow>
          <H2>Everything your teams need, connected</H2>
          <Lead2>One login, one design system, four products that actually talk to each other.</Lead2>
        </Head>
        {PLATFORM.map((row, i) => (
          <FRow key={row.h} $rev={i % 2 === 1}>
            <FText>
              <FKick>{row.k}</FKick>
              <FH3>{row.h}</FH3>
              <FP>{row.p}</FP>
              <FList>{row.list.map(li => <li key={li}><FiCheckCircle /> {li}</li>)}</FList>
            </FText>
            <FArt>{row.art}</FArt>
          </FRow>
        ))}
      </Container>
    </Section>

    {/* VALUE PROPS */}
    <Section $muted>
      <Container>
        <Head>
          <Eyebrow>Why ServiceBuds</Eyebrow>
          <H2>Serious software, without the busywork</H2>
        </Head>
        <Features>
          {[
            [FiLayers, 'Unified platform', 'Every operation in one place — no more juggling five disconnected tools.'],
            [FiShield, 'Secure by default', 'JWT auth, bcrypt-hashed passwords, and HTTPS with auto-renewing certificates.'],
            [FiKey, 'One controlled sign-in', 'Approved access per product — people see exactly what they’re cleared for.'],
            [FiZap, 'Deploy in minutes', 'Containerized and portable — spin up the whole suite on a single host.'],
            [FiGrid, 'Purpose-built', 'Each product is designed for its domain, not a generic template.'],
            [FiUserCheck, 'Team-governed access', 'Request → team review → approval. You decide who gets in, and to what.'],
          ].map(([I, t, d]: any) => (
            <Feat key={t}>
              <FeatIcon><I /></FeatIcon>
              <FeatTitle>{t}</FeatTitle>
              <FeatText>{d}</FeatText>
            </Feat>
          ))}
        </Features>
      </Container>
    </Section>

    {/* INDUSTRIES */}
    <Section $muted>
      <Container>
        <Head>
          <Eyebrow>Built for your world</Eyebrow>
          <H2>Software that speaks your industry</H2>
          <Lead2>Each product is designed around the real work of the teams that use it.</Lead2>
        </Head>
        <IndGrid>
          {INDUSTRIES.map(ind => (
            <Ind key={ind.name} $accent={ind.accent}>
              <IndIcon $accent={ind.accent}>{ind.icon}</IndIcon>
              <IndName>{ind.name}</IndName>
              <IndText>{ind.text}</IndText>
              <IndProd>{ind.product}</IndProd>
            </Ind>
          ))}
        </IndGrid>
      </Container>
    </Section>

    {/* HOW IT WORKS */}
    <Section>
      <Container>
        <Head>
          <Eyebrow>Getting started</Eyebrow>
          <H2>From request to preview in three steps</H2>
        </Head>
        <Steps>
          <Step><StepBadge $bg="#E6F2F0" $fg={T.accent}><FiSend /></StepBadge>
            <StepT>1 · Request access</StepT><StepD>Tell us who you are and pick the products you want to try.</StepD></Step>
          <StepArrow><FiArrowRight /></StepArrow>
          <Step><StepBadge $bg="#FBF1DD" $fg="#9A6510"><FiCheckCircle /></StepBadge>
            <StepT>2 · Team approves</StepT><StepD>Our team reviews and grants the apps you’re cleared for.</StepD></Step>
          <StepArrow><FiArrowRight /></StepArrow>
          <Step><StepBadge $bg="#E3F3EC" $fg="#1B7A52"><FiLogIn /></StepBadge>
            <StepT>3 · Sign in & go</StepT><StepD>Open your personalized set of products and get to work.</StepD></Step>
        </Steps>
      </Container>
    </Section>

    {/* FAQ */}
    <Section $muted>
      <Container>
        <Head>
          <Eyebrow>Questions</Eyebrow>
          <H2>Frequently asked</H2>
        </Head>
        <Faqs>
          {FAQ.map(f => (
            <FaqItem key={f.q}>
              <FaqQ>{f.q}</FaqQ>
              <FaqA>{f.a}</FaqA>
            </FaqItem>
          ))}
        </Faqs>
      </Container>
    </Section>

    {/* CTA */}
    <Container>
      <Cta>
        <CtaBlob />
        <CtaInner>
          <div>
            <CtaTitle>See ServiceBuds in action.</CtaTitle>
            <CtaSub>Request preview access — it takes about a minute.</CtaSub>
          </div>
          <PrimaryLink to="/signup" style={{ background: '#fff', color: T.accentDark, boxShadow: '0 10px 24px rgba(0,0,0,.2)' }}>
            Get started <FiArrowRight />
          </PrimaryLink>
        </CtaInner>
      </Cta>
    </Container>
  </LandingShell>
);

export default CompanyHome;

// ── Hero dashboard artwork (inline SVG) ───────────────────────────────────────
const DashboardArt: React.FC = () => (
  <svg viewBox="0 0 420 320" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <defs>
      <linearGradient id="g1" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor="#2FBFA9" /><stop offset="1" stopColor="#0E3F3A" />
      </linearGradient>
      <linearGradient id="g2" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#7FE0D0" /><stop offset="1" stopColor="#2FBFA9" />
      </linearGradient>
    </defs>
    {/* main glass panel */}
    <rect x="24" y="30" width="372" height="260" rx="18" fill="rgba(255,255,255,.10)" stroke="rgba(255,255,255,.28)" />
    <rect x="24" y="30" width="372" height="46" rx="18" fill="rgba(255,255,255,.06)" />
    <circle cx="48" cy="53" r="4" fill="#FF6B6B" /><circle cx="64" cy="53" r="4" fill="#FFC24A" /><circle cx="80" cy="53" r="4" fill="#3DDC97" />
    {/* KPI tiles */}
    <rect x="44" y="92" width="104" height="60" rx="10" fill="rgba(255,255,255,.12)" />
    <rect x="56" y="106" width="52" height="8" rx="4" fill="rgba(255,255,255,.5)" />
    <rect x="56" y="122" width="72" height="14" rx="5" fill="#fff" />
    <rect x="158" y="92" width="104" height="60" rx="10" fill="rgba(255,255,255,.12)" />
    <rect x="170" y="106" width="52" height="8" rx="4" fill="rgba(255,255,255,.5)" />
    <rect x="170" y="122" width="60" height="14" rx="5" fill="#fff" />
    {/* donut */}
    <g transform="translate(316,122)">
      <circle r="34" fill="none" stroke="rgba(255,255,255,.18)" strokeWidth="12" />
      <circle r="34" fill="none" stroke="url(#g2)" strokeWidth="12" strokeDasharray="150 214" strokeLinecap="round" transform="rotate(-90)" />
    </g>
    {/* bar chart */}
    <rect x="44" y="168" width="218" height="98" rx="10" fill="rgba(255,255,255,.08)" />
    <g fill="url(#g2)">
      <rect x="62" y="216" width="20" height="38" rx="4" /><rect x="92" y="200" width="20" height="54" rx="4" />
      <rect x="122" y="224" width="20" height="30" rx="4" /><rect x="152" y="188" width="20" height="66" rx="4" />
      <rect x="182" y="206" width="20" height="48" rx="4" /><rect x="212" y="182" width="20" height="72" rx="4" />
    </g>
    {/* line/spark card */}
    <rect x="272" y="168" width="90" height="98" rx="10" fill="rgba(255,255,255,.08)" />
    <path d="M284 240 L300 222 L314 232 L330 204 L350 214" stroke="#fff" strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    <circle cx="350" cy="214" r="4" fill="#fff" />
    {/* floating app pill */}
    <g transform="translate(300,44)">
      <rect x="0" y="0" width="86" height="30" rx="15" fill="url(#g1)" />
      <circle cx="16" cy="15" r="7" fill="#fff" /><rect x="30" y="11" width="44" height="8" rx="4" fill="rgba(255,255,255,.8)" />
    </g>
  </svg>
);

// ── styled ────────────────────────────────────────────────────────────────────
const float = keyframes`0%,100%{transform:translateY(0)}50%{transform:translateY(-14px)}`;
const drift = keyframes`0%,100%{transform:translate(0,0)}50%{transform:translate(20px,18px)}`;

const Hero = styled.header`
  position: relative; overflow: hidden;
  background: linear-gradient(135deg,#15524C 0%,#0E3F3A 55%,#0B342F 100%);
  padding: 40px 0 64px;
`;
const HeroGrid = styled.div`
  position: absolute; inset: 0; pointer-events: none; opacity: .5;
  background-image: linear-gradient(rgba(255,255,255,.05) 1px, transparent 1px),
                    linear-gradient(90deg, rgba(255,255,255,.05) 1px, transparent 1px);
  background-size: 44px 44px;
  -webkit-mask-image: radial-gradient(ellipse at 70% 0%, #000 30%, transparent 72%);
          mask-image: radial-gradient(ellipse at 70% 0%, #000 30%, transparent 72%);
`;
const HeroBlobA = styled.div`position:absolute; top:-120px; right:-80px; width:420px; height:420px; border-radius:50%;
  background:radial-gradient(circle,rgba(80,190,175,.30),transparent 70%); animation:${drift} 14s ease-in-out infinite;`;
const HeroBlobB = styled.div`position:absolute; bottom:-160px; left:-60px; width:380px; height:380px; border-radius:50%;
  background:radial-gradient(circle,rgba(80,190,175,.16),transparent 70%); animation:${drift} 18s ease-in-out infinite reverse;`;

const HeroInner = styled.div`
  position: relative; z-index: 1;
  display: grid; grid-template-columns: 1.1fr .9fr; align-items: center; gap: 40px;
  padding-top: 26px;
  @media (max-width: 900px){ grid-template-columns: 1fr; }
`;
const HeroText = styled.div``;
const Kicker = styled.div`
  display:inline-block; font-size:12px; font-weight:700; letter-spacing:.08em; text-transform:uppercase;
  color:#CFEDE6; background:rgba(255,255,255,.10); border:1px solid rgba(255,255,255,.20);
  border-radius:20px; padding:6px 14px; margin-bottom:20px;`;
const HeroTitle = styled.h1`
  font-size: 52px; line-height: 1.04; font-weight: 800; letter-spacing: -.03em; color:#fff; margin:0 0 18px;
  @media (max-width: 900px){ font-size: 38px; }`;
const Grad = styled.span`
  background: linear-gradient(90deg,#7FE0D0,#39C9B0); -webkit-background-clip:text; background-clip:text; color:transparent;`;
const HeroLead = styled.p`font-size:17px; line-height:1.6; color:rgba(214,240,235,.92); margin:0 0 28px; max-width:560px;`;
const HeroCtas = styled.div`display:flex; gap:14px; flex-wrap:wrap; margin-bottom:26px;`;
const Trust = styled.div`display:flex; align-items:center; gap:12px; flex-wrap:wrap;
  span{ font-size:12.5px; font-weight:600; color:rgba(214,240,235,.7); }`;
const Chips = styled.div`display:flex; gap:8px; flex-wrap:wrap;`;
const Chip = styled.span`display:inline-flex; align-items:center; gap:6px; font-size:12.5px; font-weight:700; color:#CFEDE6;
  background:rgba(255,255,255,.08); border:1px solid rgba(255,255,255,.16); border-radius:20px; padding:6px 11px;`;
const HeroArt = styled.div`animation:${float} 7s ease-in-out infinite;
  svg{ width:100%; height:auto; filter: drop-shadow(0 30px 50px rgba(0,0,0,.35)); }
  @media (max-width: 900px){ display:none; }`;

const Stats = styled.div`
  position: relative; z-index: 2; margin-top: -34px;
  display: grid; grid-template-columns: repeat(4,1fr); gap: 16px;
  background:#fff; border:1px solid ${T.border}; border-radius:20px; padding:22px;
  box-shadow: 0 18px 40px rgba(13,52,47,.12);
  @media (max-width: 640px){ grid-template-columns: repeat(2,1fr); }`;
const Stat = styled.div`text-align:center;`;
const StatN = styled.div`font-size:30px; font-weight:800; letter-spacing:-.02em; color:${T.accent};`;
const StatL = styled.div`font-size:12.5px; font-weight:600; color:${T.text2}; margin-top:2px;`;

const Section = styled.section<{ $muted?: boolean }>`
  padding: 64px 0;
  background: ${p => p.$muted ? 'linear-gradient(180deg,#EAEEEF,#E4EAEA)' : 'transparent'};`;
const Head = styled.div`text-align:center; max-width:640px; margin:0 auto 34px;`;
const H2 = styled.h2`font-size:30px; font-weight:800; letter-spacing:-.02em; color:${T.text}; margin:0 0 8px;
  @media (max-width: 640px){ font-size:24px; }`;
const Lead2 = styled.p`font-size:15px; color:${T.text2}; margin:0;`;
const Cards = styled.div`display:grid; grid-template-columns:repeat(auto-fill,minmax(280px,1fr)); gap:20px;`;

const Features = styled.div`display:grid; grid-template-columns:repeat(3,1fr); gap:18px;
  @media (max-width: 900px){ grid-template-columns:repeat(2,1fr); }
  @media (max-width: 560px){ grid-template-columns:1fr; }`;
const Feat = styled.div`background:#fff; border:1px solid ${T.border}; border-radius:18px; padding:24px;
  box-shadow:0 4px 14px rgba(13,52,47,.05); transition:transform .16s ease, box-shadow .16s ease;
  &:hover{ transform:translateY(-3px); box-shadow:0 14px 34px rgba(13,52,47,.13); }`;
const FeatIcon = styled.div`width:46px; height:46px; border-radius:13px; display:flex; align-items:center; justify-content:center;
  font-size:22px; color:#fff; background:linear-gradient(135deg,${T.accent},${T.accentDark}); margin-bottom:14px;
  box-shadow:0 8px 16px rgba(14,63,58,.28);`;
const FeatTitle = styled.h3`font-size:16.5px; font-weight:800; color:${T.text}; margin:0 0 6px;`;
const FeatText = styled.p`font-size:14px; line-height:1.55; color:${T.text2}; margin:0;`;

const Steps = styled.div`display:flex; align-items:stretch; justify-content:center; gap:12px; flex-wrap:wrap;`;
const Step = styled.div`flex:1; min-width:220px; max-width:300px; background:#fff; border:1px solid ${T.border};
  border-radius:18px; padding:24px; text-align:center; box-shadow:0 4px 14px rgba(13,52,47,.05);`;
const StepArrow = styled.div`display:flex; align-items:center; color:${T.muted}; font-size:22px;
  @media (max-width: 760px){ transform:rotate(90deg); }`;
const StepBadge = styled.div<{ $bg:string; $fg:string }>`width:48px; height:48px; border-radius:14px; margin:0 auto 12px;
  display:flex; align-items:center; justify-content:center; font-size:22px; color:${p=>p.$fg}; background:${p=>p.$bg};`;
const StepT = styled.h3`font-size:15.5px; font-weight:800; color:${T.text}; margin:0 0 5px;`;
const StepD = styled.p`font-size:13.5px; line-height:1.5; color:${T.text2}; margin:0;`;

const Cta = styled.div`position:relative; overflow:hidden; margin:24px 0 56px; border-radius:24px;
  background:linear-gradient(135deg,#15524C,#0E3F3A 60%,#0B342F); box-shadow:0 16px 40px rgba(14,63,58,.30);`;
const CtaBlob = styled.div`position:absolute; top:-90px; right:-40px; width:280px; height:280px; border-radius:50%;
  background:radial-gradient(circle,rgba(80,190,175,.25),transparent 70%);`;
const CtaInner = styled.div`position:relative; z-index:1; padding:36px 40px; display:flex; align-items:center;
  justify-content:space-between; gap:20px; flex-wrap:wrap;`;
const CtaTitle = styled.div`font-size:26px; font-weight:800; letter-spacing:-.02em; color:#fff;`;
const CtaSub = styled.div`font-size:14.5px; color:rgba(214,240,235,.9); margin-top:4px;`;

// ── Platform alternating-row artwork ──────────────────────────────────────────
const Frame: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <svg viewBox="0 0 360 236" fill="none" aria-hidden="true" style={{ width: '100%', height: 'auto' }}>
    <rect x="6" y="6" width="348" height="224" rx="18" fill="#fff" stroke="#E7ECEC" />
    <rect x="6" y="6" width="348" height="40" rx="18" fill="#F4F6F6" />
    <circle cx="28" cy="26" r="4" fill="#FF6B6B" /><circle cx="42" cy="26" r="4" fill="#FFC24A" /><circle cx="56" cy="26" r="4" fill="#3DDC97" />
    {children}
  </svg>
);
const RowArtWorkflow: React.FC = () => (
  <Frame>
    {[0, 1, 2].map(i => (
      <g key={i} transform={`translate(28,${72 + i * 46})`}>
        <rect width="34" height="34" rx="9" fill="#E6F2F0" /><rect x="9" y="9" width="16" height="16" rx="4" fill="#16857A" />
        <rect x="48" y="4" width="150" height="9" rx="4" fill="#243640" /><rect x="48" y="20" width="110" height="8" rx="4" fill="#C6D0CF" />
        <rect x="250" y="6" width="60" height="22" rx="11" fill="#E3F3EC" />
      </g>
    ))}
  </Frame>
);
const RowArtSecurity: React.FC = () => (
  <Frame>
    <g transform="translate(180,132)">
      <path d="M0 -56 L52 -38 V6 C52 42 30 66 0 78 C-30 66 -52 42 -52 6 V-38 Z" fill="#E6F2F0" stroke="#16857A" strokeWidth="3" />
      <rect x="-16" y="-6" width="32" height="26" rx="5" fill="#16857A" /><path d="M-9 -6 V-16 a9 9 0 0 1 18 0 V-6" fill="none" stroke="#16857A" strokeWidth="3" />
      <circle cx="0" cy="4" r="4" fill="#fff" />
    </g>
  </Frame>
);
const RowArtAccess: React.FC = () => (
  <Frame>
    {[0, 1, 2].map(i => (
      <g key={i} transform={`translate(28,${70 + i * 48})`}>
        <circle cx="17" cy="17" r="15" fill={i === 2 ? '#FBF1DD' : '#E3F3EC'} />
        <path d={i === 2 ? 'M11 17h12' : 'M10 17l5 5 9-10'} stroke={i === 2 ? '#9A6510' : '#1B7A52'} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none" />
        <rect x="46" y="6" width="140" height="9" rx="4" fill="#243640" /><rect x="46" y="21" width="96" height="8" rx="4" fill="#C6D0CF" />
        <rect x="250" y="4" width="62" height="24" rx="12" fill={i === 2 ? '#FBF1DD' : '#E3F3EC'} />
      </g>
    ))}
  </Frame>
);

const PLATFORM = [
  { k: 'Unified operations', h: 'One place for every workflow',
    p: 'Stop stitching together spreadsheets and single-purpose tools. Fees, maintenance, jobs, incidents, and patient records live in products that share one login and one design language.',
    list: ['Consistent experience across products', 'Shared sign-in and access control', 'No data trapped in disconnected silos'], art: <RowArtWorkflow /> },
  { k: 'Security first', h: 'Secure by design, not as an afterthought',
    p: 'Every product ships with token-based auth, bcrypt-hashed passwords, per-role access, and HTTPS with auto-renewing certificates — so your data and your customers’ data stay protected.',
    list: ['JWT auth + bcrypt password hashing', 'Role-based access on every endpoint', 'HTTPS everywhere, certificates auto-renewed'], art: <RowArtSecurity /> },
  { k: 'You’re in control', h: 'Grant access on your terms',
    p: 'People request access, your team reviews the full record, and you approve exactly the products each person needs. Nobody sees more than you’ve cleared them for.',
    list: ['Full signup record for every request', 'Approve or reject per product', 'Verified emails before anyone gets in'], art: <RowArtAccess /> },
];

const INDUSTRIES = [
  { name: 'Property & community', text: 'Fees, maintenance, amenities, and resident portals for condo and property teams.', product: 'Condo Manager', accent: '#2563EB', icon: <FiHome /> },
  { name: 'Pest control', text: 'CRM, scheduling, compliance logs, and smart-trap telemetry for field operators.', product: 'PestPulse Pro', accent: '#0E9F6E', icon: <FiActivity /> },
  { name: 'Health & safety', text: 'Incident tracking, corrective actions, training records, and reporting for EHS teams.', product: 'EHS Guardian', accent: '#E8730C', icon: <FiShield /> },
  { name: 'Healthcare', text: 'Patient records, scheduling, and a clinical dashboard for modern clinics.', product: 'Nexus Health', accent: '#0EA5E9', icon: <FiHeart /> },
];

const FAQ = [
  { q: 'How do I get access?', a: 'Request preview access, verify your email, and our team approves the products you’re cleared for. Then sign in to your personalized set of apps.' },
  { q: 'Do I need a separate login for each product?', a: 'No — one ServiceBuds sign-in gives you access to every product you’ve been approved for.' },
  { q: 'Is my data secure?', a: 'Yes. We use token-based authentication, bcrypt-hashed passwords, per-role access controls, and HTTPS with auto-renewing certificates across the platform.' },
  { q: 'Which products are available?', a: 'Four today: Condo Manager, PestPulse Pro, EHS Guardian, and Nexus Health — each purpose-built for its domain.' },
  { q: 'How long does approval take?', a: 'Approvals are handled by our team from the review console. Once approved, you can sign in right away.' },
];

// platform rows
const FRow = styled.div<{ $rev?: boolean }>`
  display:grid; grid-template-columns:1fr 1fr; gap:44px; align-items:center; margin:30px 0;
  direction:${p => p.$rev ? 'rtl' : 'ltr'}; & > * { direction:ltr; }
  @media (max-width:860px){ grid-template-columns:1fr; direction:ltr; gap:22px; }`;
const FText = styled.div``;
const FKick = styled.div`font-size:11.5px; font-weight:700; letter-spacing:.07em; text-transform:uppercase; color:${T.accent}; margin-bottom:8px;`;
const FH3 = styled.h3`font-size:24px; font-weight:800; letter-spacing:-.02em; color:${T.text}; margin:0 0 10px;`;
const FP = styled.p`font-size:15px; line-height:1.6; color:${T.text2}; margin:0;`;
const FList = styled.ul`list-style:none; padding:0; margin:16px 0 0; display:flex; flex-direction:column; gap:9px;
  li{ display:flex; align-items:center; gap:9px; font-size:14px; color:${T.text}; font-weight:600; }
  svg{ color:${T.accent}; font-size:17px; flex-shrink:0; }`;
const FArt = styled.div`filter: drop-shadow(0 18px 34px rgba(13,52,47,.12));`;

// industries
const IndGrid = styled.div`display:grid; grid-template-columns:repeat(4,1fr); gap:18px;
  @media (max-width:900px){ grid-template-columns:repeat(2,1fr); } @media (max-width:520px){ grid-template-columns:1fr; }`;
const Ind = styled.div<{ $accent: string }>`background:#fff; border:1px solid ${T.border}; border-top:3px solid ${p => p.$accent};
  border-radius:16px; padding:22px; box-shadow:0 4px 14px rgba(13,52,47,.05); transition:transform .16s ease, box-shadow .16s ease;
  &:hover{ transform:translateY(-3px); box-shadow:0 14px 30px rgba(13,52,47,.12); }`;
const IndIcon = styled.div<{ $accent: string }>`width:44px; height:44px; border-radius:12px; display:flex; align-items:center; justify-content:center;
  font-size:21px; color:${p => p.$accent}; background:${p => p.$accent}14; margin-bottom:13px;`;
const IndName = styled.h3`font-size:16px; font-weight:800; color:${T.text}; margin:0 0 6px;`;
const IndText = styled.p`font-size:13.5px; line-height:1.5; color:${T.text2}; margin:0 0 12px;`;
const IndProd = styled.div`font-size:12px; font-weight:700; color:${T.accent};`;

// faq
const Faqs = styled.div`max-width:760px; margin:0 auto; display:flex; flex-direction:column; gap:12px;`;
const FaqItem = styled.div`background:#fff; border:1px solid ${T.border}; border-radius:14px; padding:20px 22px;`;
const FaqQ = styled.h3`font-size:15.5px; font-weight:800; color:${T.text}; margin:0 0 7px;`;
const FaqA = styled.p`font-size:14px; line-height:1.55; color:${T.text2}; margin:0;`;
